import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { accountsService } from '@/services/accounts.service';
import type { AccountsListParams, CreateAccountDto, UpdateAccountDto } from '@/types/accounts';

/**
 * Accounts Hooks
 * React Query hooks for /api/v1/accounts/* endpoints
 */

// Query keys
export const accountKeys = {
  all: ['accounts'] as const,
  lists: () => [...accountKeys.all, 'list'] as const,
  list: (params: AccountsListParams) => [...accountKeys.lists(), params] as const,
  details: () => [...accountKeys.all, 'detail'] as const,
  detail: (id: string) => [...accountKeys.details(), id] as const,
  byUser: (userId: string, organizationId?: string) =>
    [...accountKeys.all, 'user', userId, organizationId ?? null] as const,
  organizations: (status?: 'active' | 'inactive') =>
    [...accountKeys.all, 'organization', status] as const,
};

/**
 * Hook to fetch bank accounts for a specific user (legacy)
 */
export const useBankAccounts = (userId: string | undefined, organizationId?: string) => {
  return useQuery({
    queryKey: accountKeys.byUser(userId || '', organizationId),
    queryFn: () => accountsService.getBankAccounts(userId || '', organizationId),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to list accounts with filters
 */
export const useAccounts = (params: AccountsListParams = {}) => {
  return useQuery({
    queryKey: accountKeys.list(params),
    queryFn: () => accountsService.listAccounts(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get a specific account
 */
export const useAccount = (id: string | undefined) => {
  return useQuery({
    queryKey: accountKeys.detail(id || ''),
    queryFn: () => accountsService.getAccount(id || ''),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to get organization accounts
 */
export const useOrganizationAccounts = (status: 'active' | 'inactive' = 'active') => {
  return useQuery({
    queryKey: accountKeys.organizations(status),
    queryFn: () => accountsService.getOrganizationAccounts(status),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to create an account
 */
export const useCreateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAccountDto) => accountsService.createAccount(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.all });
    },
  });
};

/**
 * Hook to update an account
 */
export const useUpdateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAccountDto }) =>
      accountsService.updateAccount(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.detail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
};

/**
 * Hook to delete an account
 */
export const useDeleteAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accountsService.deleteAccount(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
};
